import React, { useState, useEffect } from "react";
import {
  X,
  AlertTriangle,
  Calendar,
  Users,
  DollarSign,
  Trash2,
} from "lucide-react";
import Button from "../ui/Button";
import { dateDeleteService } from "../../lib/dateDeleteService";

const DeleteDateModal = ({
  isOpen,
  onClose,
  selectedDate,
  onDeleteComplete,
}) => {
  const [dateSummary, setDateSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(null);
  const [error, setError] = useState(null);

  // Load data summary when modal opens
  useEffect(() => {
    if (isOpen && selectedDate) {
      loadDateSummary();
    }
  }, [isOpen, selectedDate]);

  const loadDateSummary = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const summary = await dateDeleteService.getDateSummary(selectedDate);
      setDateSummary(summary);
    } catch (err) {
      console.error("Error loading date summary:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      setDeleteProgress(null);

      const result = await dateDeleteService.deleteDataForDate(
        selectedDate,
        (progress) => {
          setDeleteProgress(progress);
        }
      );

      // Show success and close modal
      setTimeout(() => {
        onDeleteComplete(result);
        handleClose();
      }, 1000);
    } catch (err) {
      console.error("Error deleting data:", err);
      setError(err.message);
      setIsDeleting(false);
      setDeleteProgress(null);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setDateSummary(null);
      setDeleteProgress(null);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900/95 backdrop-blur-xl border border-red-500/20 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-red-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Trash2 className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Delete Date Data</h2>
              <p className="text-sm text-gray-400">
                This action cannot be undone
              </p>
            </div>
          </div>
          {!isDeleting && (
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Date */}
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="h-5 w-5 text-red-400" />
              <span className="font-semibold text-white">Selected Date</span>
            </div>
            <p className="text-lg font-bold text-red-300">
              {selectedDate
                ? dateDeleteService.formatDate(selectedDate)
                : "No date selected"}
            </p>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-400 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading data summary...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <span className="font-semibold text-red-300">Error</span>
              </div>
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Data Summary */}
          {dateSummary && !isLoading && (
            <div className="space-y-4">
              {dateSummary.hasData ? (
                <>
                  {/* Warning */}
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      <span className="font-semibold text-yellow-300">
                        Warning
                      </span>
                    </div>
                    <p className="text-yellow-200 text-sm">
                      This will permanently delete all performance data for this
                      date. This action cannot be undone and may affect referral
                      calculations.
                    </p>
                  </div>

                  {/* Data Statistics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-white mb-1">
                        {dateSummary.recordCount.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-400">Records</div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Users className="h-4 w-4 text-blue-400" />
                        <span className="text-2xl font-bold text-white">
                          {dateSummary.affiliateCount}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">Affiliates</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <DollarSign className="h-4 w-4 text-green-400" />
                        <span className="text-lg font-bold text-white">
                          {dateSummary.formattedSpend}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">Total Spend</div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <DollarSign className="h-4 w-4 text-blue-400" />
                        <span className="text-lg font-bold text-white">
                          {dateSummary.formattedRevenue}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">Total Revenue</div>
                    </div>
                  </div>

                  {/* Affected Affiliates */}
                  {dateSummary.affiliates &&
                    dateSummary.affiliates.length > 0 && (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <h4 className="font-semibold text-white mb-2">
                          Affected Affiliates:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {dateSummary.affiliates
                            .slice(0, 10)
                            .map((affiliate, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs"
                              >
                                {affiliate}
                              </span>
                            ))}
                          {dateSummary.affiliates.length > 10 && (
                            <span className="px-2 py-1 bg-gray-500/20 text-gray-300 rounded text-xs">
                              +{dateSummary.affiliates.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="p-4 bg-gray-500/20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Calendar className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-400 text-lg font-medium">
                    No Data Found
                  </p>
                  <p className="text-gray-500 text-sm">
                    There are no records for{" "}
                    {dateDeleteService.formatDate(selectedDate)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Delete Progress */}
          {deleteProgress && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">
                  {deleteProgress.stage === "completed"
                    ? "Completed"
                    : "Deleting..."}
                </span>
                <span className="text-sm text-gray-400">
                  {deleteProgress.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${deleteProgress.progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-400">{deleteProgress.message}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isDeleting && dateSummary && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <Button
              onClick={handleClose}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              Cancel
            </Button>
            {dateSummary.hasData && (
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {dateSummary.recordCount.toLocaleString()} Records
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteDateModal;
